import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { ApiParams } from '../../../common/api/api';
import {
  getProductEntityPrefix,
  useFindDesignInputsByUserNeedId,
  useFindProductDesignOutputsByUserNeedId,
  useFindProductValidationsByUserNeedId,
  useFindProductVerificationsByUserNeedId
} from '../Product.hooks';
import {
  ProductDesignInput,
  ProductDesignOutput,
  ProductEntity,
  ProductRelationMap,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';
import { EntityManager } from './connector';
import * as Styled from './ProductBoard.styled';
import ProductBoardColumn, {
  ProductBoardColumnEntity,
  ProductBoardColumnProps
} from './ProductBoardColumn';
import ProductBoardConnector from './ProductBoardConnector';
import ProductBoardColumnActions, {
  ProductBoardColumnActionsProps
} from './ProductBoardColumnActions';

function getIds(entities: Partial<ProductEntity>[]): number[] {
  return entities.map(
    (entity: Partial<ProductEntity>) => entity.id
  ) as number[];
}

function useGetEntityIds(
  entities: Partial<ProductEntity>[] | undefined,
  entity: ProductBoardColumnEntity
): string[] {
  return useMemo(() => {
    const prefix: string = getProductEntityPrefix(entity);
    return (
      entities?.map((entity: Partial<ProductEntity>) => prefix + entity.id) ||
      []
    );
  }, [entity, entities]);
}

export interface ProductBoardSwimlaneColumnsProps {
  hidden?: boolean;
  stretch?: boolean;
  userNeed?: Partial<ProductUserNeed>;
  userNeeds?: Partial<ProductUserNeed>[];
  designInputs?: Partial<ProductDesignInput>[];
  designOutputs?: Partial<ProductDesignOutput>[];
  verifications?: Partial<ProductVerification>[];
  validations?: Partial<ProductValidation>[];
  columnProps: Omit<ProductBoardColumnProps, 'entity'>;
  fetchParams?: ApiParams;
  relationMap?: ProductRelationMap[];
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
  actionsProps?: ProductBoardColumnActionsProps;
  handleIds?: (ids: number[], entity: ProductBoardColumnEntity) => void;
  handleFetch?: (entity: ProductBoardColumnEntity) => void;
}

const ProductBoardSwimlaneColumns: React.FC<ProductBoardSwimlaneColumnsProps> = (
  props: ProductBoardSwimlaneColumnsProps
) => {
  const {
    hidden = false,
    stretch = false,
    userNeed,
    userNeeds: initialUserNeeds,
    designInputs: initialDesignInputs,
    designOutputs: initialDesignOutputs,
    verifications: initialVerifications,
    validations: initialValidations,
    relationMap,
    scrollRef,
    fetchParams,
    actionsProps,
    columnProps: initialColumnProps,
    handleIds,
    handleFetch: handleFetchProp
  } = props;
  const columnProps: Omit<ProductBoardColumnProps, 'entity'> = {
    ...initialColumnProps,
    collapsed: actionsProps?.collapsed
  };
  const productId: number = columnProps.product.id;

  const rootRef = useRef<HTMLDivElement | null>();
  const [manager, setManager] = useState<EntityManager | undefined>();

  const productUserNeeds: Partial<ProductUserNeed>[] = useMemo(() => {
    return userNeed ? [userNeed] : [];
  }, [userNeed]);

  const {
    productDesignInputs,
    findProductDesignInputs
  } = useFindDesignInputsByUserNeedId(productId, userNeed?.id || 0);
  const {
    productDesignOutputList,
    findProductDesignOutputs
  } = useFindProductDesignOutputsByUserNeedId(productId, userNeed?.id || 0);
  const {
    productVerificationList,
    findProductVerifications
  } = useFindProductVerificationsByUserNeedId(productId, userNeed?.id || 0);
  const {
    productValidationList,
    findProductValidations
  } = useFindProductValidationsByUserNeedId(productId, userNeed?.id || 0);

  const userNeeds: Partial<ProductUserNeed>[] | undefined =
    initialUserNeeds || productUserNeeds;
  const designInputs: Partial<ProductDesignInput>[] | undefined =
    initialDesignInputs || productDesignInputs;
  const designOutputs: Partial<ProductDesignOutput>[] | undefined =
    initialDesignOutputs || productDesignOutputList;
  const verifications: Partial<ProductVerification>[] | undefined =
    initialVerifications || productVerificationList;
  const validations: Partial<ProductValidation>[] | undefined =
    initialValidations || productValidationList;

  // get entity ids to filter relationMap
  const userNeedIds: string[] = useGetEntityIds(
    userNeeds,
    ProductBoardColumnEntity.UserNeed
  );
  const designInputIds: string[] = useGetEntityIds(
    designInputs,
    ProductBoardColumnEntity.DesignInput
  );
  const designOutputIds: string[] = useGetEntityIds(
    designOutputs,
    ProductBoardColumnEntity.DesignOutput
  );
  const verificationIds: string[] = useGetEntityIds(
    verifications,
    ProductBoardColumnEntity.Verification
  );
  const validationIds: string[] = useGetEntityIds(
    validations,
    ProductBoardColumnEntity.Validation
  );

  const filteredRelationMap: ProductRelationMap[] | undefined = useMemo(() => {
    const allIds: string[] = ([] as string[]).concat(
      userNeedIds,
      designInputIds,
      designOutputIds,
      verificationIds,
      validationIds
    );
    return relationMap?.filter((map: ProductRelationMap) => {
      // should include both from and to
      return allIds.includes(map.from) && allIds.includes(map.to);
    });
  }, [
    relationMap,
    userNeedIds,
    designInputIds,
    designOutputIds,
    verificationIds,
    validationIds
  ]);

  const handleFetch = useCallback(
    (entity: ProductBoardColumnEntity) => {
      handleFetchProp?.(entity);
      switch (entity) {
        case ProductBoardColumnEntity.DesignInput:
          findProductDesignInputs(fetchParams);
          break;
        case ProductBoardColumnEntity.DesignOutput:
          findProductDesignOutputs(fetchParams);
          break;
        case ProductBoardColumnEntity.Verification:
          findProductVerifications(fetchParams);
          break;
        case ProductBoardColumnEntity.Validation:
          findProductValidations(fetchParams);
          break;
      }
    },
    [
      fetchParams,
      handleFetchProp,
      findProductDesignInputs,
      findProductDesignOutputs,
      findProductVerifications,
      findProductValidations
    ]
  );

  // fetch entities

  useEffect(() => {
    if (userNeed && !initialDesignInputs) {
      findProductDesignInputs(fetchParams);
    }
  }, [userNeed, fetchParams, initialDesignInputs, findProductDesignInputs]);

  useEffect(() => {
    if (userNeed && !initialDesignOutputs) {
      findProductDesignOutputs(fetchParams);
    }
  }, [userNeed, fetchParams, initialDesignOutputs, findProductDesignOutputs]);

  useEffect(() => {
    if (userNeed && !initialVerifications) {
      findProductVerifications(fetchParams);
    }
  }, [userNeed, fetchParams, initialVerifications, findProductVerifications]);

  useEffect(() => {
    if (userNeed && !initialValidations) {
      findProductValidations(fetchParams);
    }
  }, [userNeed, fetchParams, initialValidations, findProductValidations]);

  // id handlers

  useEffect(() => {
    if (productDesignInputs) {
      handleIds?.(
        getIds(productDesignInputs),
        ProductBoardColumnEntity.DesignInput
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDesignInputs]);

  useEffect(() => {
    if (productDesignOutputList) {
      handleIds?.(
        getIds(productDesignOutputList),
        ProductBoardColumnEntity.DesignOutput
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDesignOutputList]);

  useEffect(() => {
    if (productVerificationList) {
      handleIds?.(
        getIds(productVerificationList),
        ProductBoardColumnEntity.Verification
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productVerificationList]);

  useEffect(() => {
    if (productValidationList) {
      handleIds?.(
        getIds(productValidationList),
        ProductBoardColumnEntity.Validation
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productValidationList]);

  useLayoutEffect(() => {
    const { current: rootEl } = rootRef;
    const { current: scrollEl } = scrollRef || {};
    if (rootEl) {
      setManager(new EntityManager(rootEl, scrollEl));
    }
  }, [scrollRef]);

  const className: string = useMemo(() => {
    const classes: string[] = ['product-board', 'swimlane'];
    if (stretch) {
      classes.push('stretch');
    }
    if (hidden) {
      classes.push('hidden');
    }
    return classes.join(' ');
  }, [stretch, hidden]);

  return (
    <Styled.ProductBoard
      className={className}
      ref={(ref: HTMLDivElement | null) => (rootRef.current = ref)}
    >
      {manager && (
        <Styled.ConnectorContainer className='connector-container'>
          {filteredRelationMap?.map((relation: ProductRelationMap) => (
            <ProductBoardConnector
              {...relation}
              manager={manager}
              key={`${relation.from}-${relation.to}`}
            />
          ))}
        </Styled.ConnectorContainer>
      )}

      <ProductBoardColumnActions
        {...actionsProps}
        hideHeader={columnProps.hideHeader}
      />
      <ProductBoardColumn
        {...columnProps}
        userNeeds={userNeeds}
        entity={ProductBoardColumnEntity.UserNeed}
        handleFetch={() => handleFetch(ProductBoardColumnEntity.UserNeed)}
      />
      <ProductBoardColumn
        {...columnProps}
        designInputs={designInputs}
        entity={ProductBoardColumnEntity.DesignInput}
        handleFetch={() => handleFetch(ProductBoardColumnEntity.DesignInput)}
      />
      <ProductBoardColumn
        {...columnProps}
        designOutputs={designOutputs}
        entity={ProductBoardColumnEntity.DesignOutput}
        handleFetch={() => handleFetch(ProductBoardColumnEntity.DesignOutput)}
      />
      <ProductBoardColumn
        {...columnProps}
        verifications={verifications}
        entity={ProductBoardColumnEntity.Verification}
        handleFetch={() => handleFetch(ProductBoardColumnEntity.Verification)}
      />
      <ProductBoardColumn
        {...columnProps}
        validations={validations}
        entity={ProductBoardColumnEntity.Validation}
        handleFetch={() => handleFetch(ProductBoardColumnEntity.Validation)}
      />
    </Styled.ProductBoard>
  );
};

export default ProductBoardSwimlaneColumns;
